import { AHText, ListItem } from "arkh";
import React, { SetStateAction, useCallback, useLayoutEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { SelectListItem } from "arkh/src/cross-platform/SelectList/SelectListItem";

import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { RoutineSectionListItem } from "./RoutineSectionListItem";
import {
  TaskListItemIcon,
  TaskListItemTitle,
} from "../../../../../components/TaskListItem";
import { ListSectionHeaderRoutine } from "./ListSectionHeaderRoutine";
import { SelectList } from "arkh/src/cross-platform/SelectList/SelectList";
import {
  RoutineType,
  WidgetTaskSingleSettingsScreenComponent_task$key,
} from "./__generated__/WidgetTaskSingleSettingsScreenComponent_task.graphql";
import { WidgetTaskSingleSettingsScreenComponentTask_task$key } from "./__generated__/WidgetTaskSingleSettingsScreenComponentTask_task.graphql";
import { useNavigation } from "@react-navigation/native";
import { useModalInsets } from "../../../../../components/mobile/useModalInsets";

type Props = {
  storecastId: string;
  taskId: string;
  sectionIds: string[];
  setSectionIds(_: SetStateAction<string[]>): void;
  task: WidgetTaskSingleSettingsScreenComponent_task$key;
};

export function WidgetTaskSingleSettingsScreenComponent(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWidgetTaskSingleSettingsScreenComponent
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWidgetTaskSingleSettingsScreenComponent(
  props: Props & {
    fetchKey: string;
  }
) {
  const task = useFragment(
    graphql`
      fragment WidgetTaskSingleSettingsScreenComponent_task on Task {
        id
        routine {
          type
        }
        ...WidgetTaskSingleSettingsScreenComponentTask_task
        activeSectionVersions {
          id
          section {
            id
          }
          ...RoutineSectionListItem_RoutineSectionVersion
        }
      }
    `,
    props.task
  );

  const onPressItem = useCallback(
    (itemId: string, selected: boolean) => {
      if (props.sectionIds.length === 0) {
        props.setSectionIds([itemId]);
      } else if (selected) {
        props.setSectionIds((ids) => ids.filter((id) => id !== itemId));
      } else {
        props.setSectionIds((ids) => [...ids, itemId]);
      }
    },
    [props]
  );

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof task.activeSectionVersions)[number];
      index: number;
    }) => {
      const selected = props.sectionIds.includes(item.section.id);
      return (
        <RoutineSectionListItem
          isFirst={index === 0}
          isLast={index === task.activeSectionVersions.length - 1}
          onPress={() => {
            onPressItem(item.section.id, selected);
          }}
          section={item}
          right={<ListItem.SelectIndicator selected={selected} />}
        />
      );
    },
    [task, props.sectionIds, onPressItem]
  );

  const intl_tokens = useIntlTokens();

  const navigation = useNavigation();
  useLayoutEffect(() => {
    navigation.setOptions({
      title:
        task.routine.type === "KPI" ? intl_tokens.SelectMeasurementAreas : "",
    });
  }, [
    intl_tokens.SelectMeasurementAreas,
    intl_tokens.SelectSections,
    navigation,
    task.routine.type,
  ]);

  const insets = useModalInsets();

  return (
    <SelectList
      contentBox={{
        spaceTop: "medium",
      }}
      insets={insets}
      ListHeaderComponent={
        <>
          {task.routine.type === "DEFAULT" ? (
            <>
              <WidgetTaskSingleSettingsScreenComponentTask task={task} />
              {task.activeSectionVersions.length > 1 ? (
                <AHText
                  type="title-small"
                  spaceTop="large"
                  spaceHorizontal="small"
                  spaceBottom="small"
                >
                  {intl_tokens.SelectSections}
                </AHText>
              ) : null}
            </>
          ) : null}
          {task.activeSectionVersions.length > 1 ? (
            <>
              <SectionSelectListSelectAllOption
                routineType={task.routine.type}
                sectionIds={props.sectionIds}
                setSectionIds={props.setSectionIds}
              />
            </>
          ) : null}
        </>
      }
      data={
        task.routine.type === "DEFAULT" && task.activeSectionVersions.length < 2
          ? []
          : task.activeSectionVersions
      }
      renderItem={renderItem}
    />
  );
}

const SectionSelectListSelectAllOption = (props: {
  setSectionIds(_: string[]): void;
  sectionIds: string[];
  routineType: RoutineType;
}) => {
  const intl_tokens = useIntlTokens();
  return (
    <SelectListItem
      selected={props.sectionIds.length === 0}
      isFirst
      isLast
      onPress={() => {
        props.setSectionIds([]);
      }}
    >
      <AHText type="title-small" style={{ flex: 1 }}>
        {props.routineType === "KPI"
          ? intl_tokens.AllMesurementAreas
          : intl_tokens.AllSections}
      </AHText>
    </SelectListItem>
  );
};

function WidgetTaskSingleSettingsScreenComponentTask(props: {
  task: WidgetTaskSingleSettingsScreenComponentTask_task$key;
}) {
  const task = useFragment(
    graphql`
      fragment WidgetTaskSingleSettingsScreenComponentTask_task on Task {
        ...TaskListItemIcon_Task
        ...TaskListItemTitle_Task
        routine {
          ...ListSectionHeaderRoutine_Routine
        }
      }
    `,
    props.task
  );

  return (
    <>
      <ListSectionHeaderRoutine routine={task.routine} />
      <ListItem isFree disabled left={<TaskListItemIcon task={task} />}>
        <TaskListItemTitle task={task} />
      </ListItem>
    </>
  );
}
